<template>
  <div
    class="group relative bg-card/50 rounded-xl border border-border/50 overflow-hidden hover:border-border transition-all duration-300 hover:shadow-lg hover:shadow-background/20 flex h-full"
    :class="{
      'flex-col hover:-translate-y-1': layout === 'grid' || layout === 'compact',
      'flex-row': layout === 'list'
    }"
  >
    <!-- Vehicle Image -->
    <div class="overflow-hidden relative"
      :class="[
        layout === 'list' ? 'w-2/5 min-w-[240px] max-w-[320px]' : 'aspect-[16/10] w-full'
      ]">
      <!-- Loading placeholder with blur effect -->
      <div
        v-if="!imageLoaded"
        class="absolute inset-0 bg-muted/50 animate-pulse flex items-center justify-center"
      >
        <div class="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>

      
      <!-- Low quality placeholder (blurred) -->
      <img
        v-if="vehicle.thumb && !imageLoaded"
        :src="vehicle.thumb"
        :alt="vehicle.title ? `${vehicle.title} - low quality placeholder` : 'Image placeholder'"
        class="absolute inset-0 w-full h-full object-cover transform scale-110 blur-sm opacity-60"
        loading="lazy"
        fetchpriority="low"
      />

      <!-- High quality image -->
      <img
        v-if="vehicle.image || vehicle.thumb"
        :src="vehicle.image || vehicle.thumb"
        :alt="vehicle.title"
        loading="lazy"
        decoding="async"
        fetchpriority="high"
        class="w-full h-full object-cover transform group-hover:scale-105 transition-all duration-500"
        :class="{ 'opacity-0': !imageLoaded }"
        @load="onImageLoad"
        @error="onImageError"
      />
    

      <!-- Quick View Button -->
      <button
        @click="openQuickView(vehicle.id)"
        class="absolute inset-0 flex items-center justify-center bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      >
        <span class="px-4 py-2 bg-card/90 rounded-lg text-sm font-medium text-card-foreground hover:bg-muted/90 transition-colors">
          Quick View
        </span>
      </button>

      <!-- Condition Badge -->
      <div v-if="vehicle.condition?.displayValue?.[0]"
        class="absolute top-4 left-4 px-2 py-1 rounded-full text-xs font-medium"
        :class="{
          'bg-green-500/90 text-white': vehicle.condition.value[0] === 'new',
          'bg-blue-500/90 text-white': vehicle.condition.value[0] === 'demo',
          'bg-orange-500/90 text-white': vehicle.condition.value[0] === 'used'
        }"
      >
        {{ vehicle.condition.displayValue[0] }}
      </div>

      <!-- New Arrival Badge -->
      <div v-if="isNewArrival(vehicle)"
        class="absolute top-2 left-2 px-2 py-1 rounded-full bg-red-500/90 text-white text-xs font-medium"
      >
        New Arrival
      </div>
    </div>

    <!-- Vehicle Info -->
    <div class="flex flex-col flex-grow p-4" :class="{ 'w-3/5 min-w-[300px]': layout === 'list' }">
      <h3 class="text-lg font-medium text-foreground line-clamp-2 group-hover:text-foreground transition-colors">{{ vehicle.title }}</h3>

      <div class="flex items-center space-x-4 text-sm text-muted-foreground mt-2">
        <span v-if="vehicle.year?.displayValue?.[0]">{{ vehicle.year?.displayValue?.[0] }}</span>
        <span v-if="vehicle.transmission?.displayValue?.[0]">{{ vehicle.transmission?.displayValue?.[0] }}</span>
        <span v-if="vehicle.kms">{{ vehicle.kms?.toLocaleString() }} km</span>
      </div>

      <!-- Additional details for list view -->
      <div v-if="layout === 'list'" class="mt-2 text-sm text-muted-foreground">
        <div class="flex flex-wrap gap-x-4 gap-y-1">
          <span v-if="vehicle.fuel?.displayValue?.[0]">
            <span class="font-medium">Fuel:</span> {{ vehicle.fuel?.displayValue?.[0] }}
          </span>
          <span v-if="vehicle.body?.displayValue?.[0]">
            <span class="font-medium">Body:</span> {{ vehicle.body?.displayValue?.[0] }}
          </span>
          <span v-if="vehicle.color?.displayValue?.[0]">
            <span class="font-medium">Color:</span> {{ vehicle.color?.displayValue?.[0] }}
          </span>
        </div>
      </div>

      <!-- Price Section -->
      <div v-if="vehicle.price && !isNaN(vehicle.price)" class="mt-auto pt-3">
        <!-- Total Price -->
        <div class="flex items-baseline">
          <span class="text-2xl font-semibold text-foreground group-hover:text-foreground transition-colors">${{ vehicle.price?.toLocaleString() }}</span>
          <span class="text-sm text-muted-foreground ml-1">drive away</span>
        </div>
        <!-- Per Week Price -->
        <div v-if="vehicle.perweek || vehicle.price" class="flex items-baseline">
          <span class="text-lg font-medium text-foreground/80">${{ getWeeklyPayment(vehicle.perweek, vehicle.price) }}</span>
          <span class="text-sm text-muted-foreground ml-1">per week</span>
        </div>
      </div>
    </div>

    <!-- Action Buttons - Separate container for footer -->
    <div class="px-4 pb-4 pt-2 mt-auto" :class="{ 'min-w-[260px]': layout === 'list' }">
      <!-- Grid/Compact layout: horizontal buttons -->
      <div v-if="layout === 'grid' || layout === 'compact'" class="flex items-center justify-between space-x-2 border-t border-border/20 pt-3">
        <button
          @click="navigateToVehicle(vehicle.id)"
          class="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-300"
        >
          View Details
        </button>
        <div class="flex items-center space-x-2">
          <button
            @click.stop="toggleFavorite(vehicle.id)"
            class="p-2 rounded-full bg-muted/50 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-300 hover:scale-110"
            :class="{ 'bg-destructive/10 hover:bg-destructive/20': isFavorite }"
            :title="isFavorite ? 'Remove from favorites' : 'Add to favorites'"
          >
            <HeartIcon
              :class="[
                'h-5 w-5 transition-colors duration-300',
                isFavorite ? 'text-destructive' : 'text-muted-foreground group-hover:text-muted-foreground/80'
              ]"
            />
          </button>
          <ClientOnly>
            <button
              @click.stop="addToCart(vehicle.id)"
              class="p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-300 hover:scale-110"
              :class="{
                'bg-blue-100 dark:bg-blue-900/30': isInCart,
                'bg-muted/50 hover:bg-muted': !isInCart
              }"
              :title="isInCart ? 'Remove from cart' : 'Add to cart'"
              :key="`cart-btn-${vehicle.id}-${isInCart}`"
            >
              <ShoppingCartIcon
                 class="h-5 w-5 transition-colors duration-300"
                 :class="isInCart ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground group-hover:text-muted-foreground/80'"
              />
              <span class="sr-only">{{ isInCart ? 'Remove from cart' : 'Add to cart' }}</span>
            </button>
            <template #fallback>
              <button class="p-2 rounded-full bg-muted/50" disabled>
                 <ShoppingCartIcon class="h-5 w-5 text-muted-foreground" />
              </button>
            </template>
          </ClientOnly>
        </div>
      </div>

      <!-- List layout: horizontal buttons -->
      <div v-else class="flex items-center space-x-3 border-t border-border/20 pt-3">
        <button
          @click="navigateToVehicle(vehicle.id)"
          class="flex-1 min-w-[120px] px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-300 flex items-center justify-center"
        >
          View Details
        </button>
        <div class="flex items-center space-x-3">
          <button
            @click.stop="toggleFavorite(vehicle.id)"
            class="p-2 w-10 h-10 rounded-full bg-muted/50 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-300 hover:scale-110 flex items-center justify-center"
            :class="{ 'bg-destructive/10 hover:bg-destructive/20': isFavorite }"
            :title="isFavorite ? 'Remove from favorites' : 'Add to favorites'"
          >
            <HeartIcon
              :class="[
                'h-5 w-5 transition-colors duration-300',
                isFavorite ? 'text-destructive' : 'text-muted-foreground group-hover:text-muted-foreground/80'
              ]"
            />
          </button>
          <ClientOnly>
            <button
              @click.stop="addToCart(vehicle.id)"
              class="p-2 mr-5 w-10 h-10 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-300 hover:scale-110 flex items-center justify-center"
              :class="{
                'bg-blue-100 dark:bg-blue-900/30': isInCart,
                'bg-muted/50 hover:bg-muted': !isInCart
              }"
              :title="isInCart ? 'Remove from cart' : 'Add to cart'"
              :key="`cart-btn-${vehicle.id}-${isInCart}`"
            >
              <ShoppingCartIcon
                 class="h-5 w-5 transition-colors duration-300"
                 :class="isInCart ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground group-hover:text-muted-foreground/80'"
              />
            </button>
            <template #fallback>
              <button class="p-2 w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center" disabled>
                 <ShoppingCartIcon class="h-5 w-5 text-muted-foreground" />
              </button>
            </template>
          </ClientOnly>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVehicleQuickView } from '~/layers/base/composables/useVehicleQuickView';
import { useRouter } from '#imports';
import { useFavoritesStore } from '~/stores/favorites';
import { useCartStore } from '~/stores/cartStore';
import { HeartIcon, ShoppingCartIcon } from '@heroicons/vue/24/outline';
import { computed, ref, onMounted } from 'vue';
import { calculateWeeklyPayment } from '~/layers/base/utils/finance';
import { useVehicleScrollPosition } from '~/layers/base/composables/useVehicleScrollPosition';

// Composables
const { openQuickView } = useVehicleQuickView();
const router = useRouter();
const favoritesStore = useFavoritesStore();
const cartStore = useCartStore();
const { saveScrollPosition } = useVehicleScrollPosition();

// Props definition
const props = defineProps({
  vehicle: {
    type: Object,
    required: true,
    default: () => ({
      id: '',
      slug: '',
      title: '',
      description: '',
      image: '',
      year: null,
      mileage: null,
      price: null,
      perweek: null,
      fuel: null
    })
  },
  layout: {
    type: String,
    default: 'grid',
    validator: (value: string) => ['grid', 'list', 'compact'].includes(value)
  }
});

// Image loading state
const imageLoaded = ref(false);
const imageError = ref(false);

// Handle image load event
const onImageLoad = () => {
  imageLoaded.value = true;
};

// Handle image error event
const onImageError = () => {
  imageError.value = true;
  imageLoaded.value = true; // Consider it "loaded" to remove loading spinner
};

// Check if image is already cached on mount
onMounted(() => {
  // Check if the image might already be in browser cache
  if (props.vehicle.thumb || props.vehicle.image) {
    const img = new Image();
    img.onload = onImageLoad;
    img.onerror = onImageError;
    // Prioritize vehicle.image for cache check, then fallback to vehicle.thumb
    img.src = props.vehicle.image || props.vehicle.thumb;

    // If the image is already cached, the onload event might not fire
    // Set a timeout to check if the image is complete
    setTimeout(() => {
      if (img.complete) {
        onImageLoad();
      }
    }, 50);
  }
});

// Reactive computed property for favorite status
const isFavorite = computed(() => {
  return favoritesStore.isFavorite(props.vehicle.id);
});

// Reactive computed property for cart status
const isInCart = computed(() => {
  return cartStore.isInCart(props.vehicle.id);
});

// Function to check if vehicle is new arrival (less than 7 days old)
const isNewArrival = (vehicle: any): boolean => {
  if (vehicle.created_at || vehicle.added_at) {
    const addedDate = new Date(vehicle.added_at || vehicle.created_at);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - addedDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  }
  return false;
}

// Function to navigate to vehicle detail page
const navigateToVehicle = (vehicleId: string) => {
  // Save scroll position before navigating
  saveScrollPosition(vehicleId);

  const vehicle = props.vehicle;
  const slug = vehicle.slug || getVehicleSlug(vehicle);
  router.push(`/vehicle-for-sale/${vehicle.stockid || vehicleId}/${slug}`);
}

// Function to toggle favorite status
const toggleFavorite = (vehicleId: string) => {
  favoritesStore.toggleFavorite(vehicleId);
}

// Function to add/remove vehicle from cart
const addToCart = (vehicleId: string) => {
  // Use cartStore action - now toggles items in/out of cart
  cartStore.addToCart(vehicleId);
}

// Helper to generate a slug if one doesn't exist
const getVehicleSlug = (vehicle: any) => {
  const title = vehicle.title ||
    (vehicle.make?.displayValue?.[0] && vehicle.model?.displayValue?.[0] ?
      `${vehicle.make.displayValue[0]} ${vehicle.model.displayValue[0]}` :
      'Unknown Vehicle');

  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Function to get the weekly payment amount using the same calculation as the filter
const getWeeklyPayment = (perweek: any, price: number): string => {
  // First try to use the perweek value if it's already calculated
  const weeklyValue = Number(perweek ?? 0);
  if (weeklyValue > 0) return weeklyValue.toLocaleString();

  // If perweek is not available, use the same calculation as the filter logic
  if (price > 0) {
    const calculated = calculateWeeklyPayment(price);
    return calculated > 0 ? calculated.toLocaleString() : '0';
  }

  // Last resort fallback calculation (should not reach here)
  return Math.round(price / 52).toLocaleString();
}
</script>

<style scoped>
.vehicle-card-animation {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.5s ease-out forwards;
  transition: all 0.4s ease;
}

.vehicle-card-animation:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px -8px rgba(0, 0, 0, 0.15);
}

.dark .vehicle-card-animation:hover {
  box-shadow: 0 15px 30px -8px rgba(0, 0, 0, 0.5);
}

/* Image loading transitions */
img {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

/* Ensure the aspect ratio container maintains proper dimensions */
.aspect-\[16\/10\] {
  position: relative;
}

.aspect-\[16\/10\]::before {
  content: "";
  display: block;
  padding-top: 62.5%; /* 10/16 = 0.625 = 62.5% */
}

.aspect-\[16\/10\] > img,
.aspect-\[16\/10\] > button.absolute.inset-0 {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* List view specific styles */
.flex-row > div.overflow-hidden {
  height: 100%;
}

.flex-row > div.overflow-hidden > img {
  height: 100%;
  object-fit: cover;
}

/* Ensure condition badges stay small and in the corner */
.aspect-\[16\/10\] > div[class*="absolute top-2"] {
  width: auto;
  height: auto;
  z-index: 2;
}

/* Loading animation */
@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.8;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .aspect-\[16\/10\]::before {
    padding-top: 75%; /* Slightly taller aspect ratio on mobile */
  }

  /* Stack list view on mobile */
  .flex-row {
    flex-direction: column;
  }

  .flex-row > div.overflow-hidden {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    aspect-ratio: 16/10;
  }

  .flex-row > div.flex-col {
    width: 100% !important;
  }
}

/* Medium screens - adjust list view proportions */
@media (min-width: 641px) and (max-width: 1023px) {
  .flex-row > div.overflow-hidden {
    width: 40% !important; /* Slightly larger image on medium screens */
    min-width: 220px !important;
  }

  .flex-row > div.flex-col.flex-grow {
    width: 60% !important;
  }

  /* Ensure buttons have enough space */
  .flex-row .px-4.pb-4 button.flex-1 {
    min-width: 120px;
  }

  .flex-row .px-4.pb-4 .space-x-3 {
    min-width: 90px;
  }
}

/* Large screens - ensure proper spacing */
@media (min-width: 1024px) {
  /* Ensure buttons have enough space */
  .flex-row .px-4.pb-4 button.flex-1 {
    min-width: 140px;
  }

  .flex-row .px-4.pb-4 .space-x-3 {
    min-width: 100px;
  }

  /* Ensure action buttons container has enough width */
  .flex-row .px-4.pb-4 {
    min-width: 260px;
  }
}
</style>
